import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import useApiContext from 'hooks/useApiContext';
import { sectionTitle, SwitchField } from './common';

const DESCRIPTIONS: Record<string, string> = {
  ticketing:
    "Inscription des visiteurs, paiement, e-billets, scan à l'entrée et données visiteurs.",
  matchmaking:
    "Rendez-vous d'affaires entre visiteurs et exposants : ouvre bbmatch depuis le menu du salon, sans nouvelle connexion.",
};

/**
 * Switches of the optional modules of an event, shown in the event settings
 * to its administrators. Turning one on makes its menu entry appear.
 */
export default function EventModules({
  eventId,
}: {
  eventId: string;
}): JSX.Element | null {
  const { ticketingRepository } = useApiContext();
  const [modules, setModules] = useState<Record<string, boolean> | null>(null);
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [canConfigure, setCanConfigure] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    ticketingRepository
      .getModules(eventId)
      .then((r) => {
        setModules(r.modules);
        setLabels(r.labels ?? {});
        setCanConfigure(!!r.can_configure);
      })
      .catch(() => setModules(null));
  }, [eventId]);

  if (!modules || !canConfigure) return null;

  const toggle = (module: string, value: boolean): void => {
    setError(null);
    setModules({ ...modules, [module]: value });
    ticketingRepository
      .setModule(eventId, module, value)
      .then((r) => {
        setModules(r.modules);
        window.dispatchEvent(new CustomEvent('event-modules-changed'));
      })
      .catch((e: Error) => {
        setModules({ ...modules });
        setError(e.message);
      });
  };

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        border: '1px solid #eee',
        borderRadius: '10px',
        p: 2.5,
        mt: 3,
      }}
    >
      <Typography sx={{ ...sectionTitle, mb: 0.5 }}>
        Modules du salon
      </Typography>
      <Typography sx={{ fontSize: 13, color: '#666', mb: 1.5 }}>
        Activez les modules utilisés pour ce salon : leur menu apparaît à
        gauche.
      </Typography>
      {Object.entries(modules).map(([key, on]) => (
        <Box key={key} sx={{ py: 1, borderTop: '1px solid #f3f3f3' }}>
          <SwitchField
            label={<b>{labels[key] ?? key}</b>}
            checked={on}
            onChange={(value) => toggle(key, value)}
            hint={DESCRIPTIONS[key]}
          />
        </Box>
      ))}
      {error && (
        <Typography sx={{ color: '#b91c1c', fontSize: 13, mt: 1 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}
